// UploadManager - Handles file uploads with support for simple and multipart uploads
import { apiService } from './api';

// Upload state interface
export interface UploadState {
  progress: number;                    // 0-100
  status: 'idle' | 'uploading' | 'success' | 'error' | 'paused';
  error: string | null;
  uploadedBytes: number;
  totalBytes: number;
  estimatedTimeRemaining: number | null;  // milliseconds
  uploadType: 'simple' | 'multipart' | null;
  uploadId: string | null;              // For multipart uploads
  uploadedParts: UploadedPart[];        // For multipart uploads
}

// Uploaded part interface for multipart uploads
export interface UploadedPart {
  PartNumber: number;
  ETag: string;
}

// Constants
const MULTIPART_THRESHOLD = 100 * 1024 * 1024; // 100MB
const MAX_FILE_SIZE = 5 * 1024 * 1024 * 1024;  // 5GB
const CHUNK_SIZE = 5 * 1024 * 1024;             // 5MB
const MAX_CONCURRENT_UPLOADS = 4;
const MAX_RETRIES = 3;

export class UploadManager {
  private file: File;
  private bucketName: string;
  private objectKey: string;
  private state: UploadState;
  private abortController: AbortController;
  private startTime: number;
  private onStateChange?: (state: UploadState) => void;

  private contentType: string;

  constructor(file: File, bucketName: string, onStateChange?: (state: UploadState) => void, prefix?: string) {
    this.file = file;
    this.bucketName = bucketName;
    const relativePath = (file as any).webkitRelativePath || file.name;
    this.objectKey = prefix ? `${prefix}/${relativePath}` : relativePath;
    this.contentType = file.type || 'application/octet-stream';
    this.onStateChange = onStateChange;
    this.abortController = new AbortController();
    this.startTime = 0;
    
    // Initialize state
    this.state = {
      progress: 0,
      status: 'idle',
      error: null,
      uploadedBytes: 0,
      totalBytes: file.size,
      estimatedTimeRemaining: null,
      uploadType: null,
      uploadId: null,
      uploadedParts: [],
    };
  }

  // Get current state
  getState(): UploadState {
    return { ...this.state };
  }

  // Main upload method
  async upload(): Promise<void> {
    try {
      // Validate file size
      if (this.file.size > MAX_FILE_SIZE) {
        throw new Error(`File size exceeds ${MAX_FILE_SIZE / (1024 * 1024 * 1024)}GB limit`);
      }

      this.startTime = Date.now();
      this.updateState({ status: 'uploading' });

      // Determine upload strategy based on file size
      if (this.file.size < MULTIPART_THRESHOLD) {
        await this.simpleUpload();
      } else {
        await this.multipartUpload();
      }

      this.updateState({ status: 'success', progress: 100 });
    } catch (error) {
      if (error instanceof Error && (error.name === 'AbortError' || error.message === 'Upload aborted' || error.message === 'AbortError')) {
        this.updateState({ status: 'paused', error: 'Upload cancelled' });
      } else {
        this.updateState({ 
          status: 'error', 
          error: error instanceof Error ? error.message : 'Upload failed' 
        });
      }
      throw error;
    }
  }

  // Simple upload for files < 100MB
  private async simpleUpload(): Promise<void> {
    this.updateState({ uploadType: 'simple' });

    try {
      // Request presigned URL (HTTP S3 endpoint)
      const response = await apiService.generatePresignedUpload(
        this.bucketName,
        this.objectKey,
        this.contentType,
        this.file.size
      );

      if (response.uploadType !== 'simple') {
        throw new Error('Expected simple upload response');
      }

      await this.uploadWithRetry(response.presignedUrl, this.file, this.contentType);
    } catch (error: any) {
      if (error.statusCode === 403 && (error.message?.includes('Storage limit exceeded') || error.message?.includes('Storage quota') || error.message?.includes('quota exceeded'))) {
        const quotaError: any = new Error(error.message || 'Storage limit exceeded. Please upgrade your plan to continue uploading.');
        quotaError.isStorageLimitError = true;
        quotaError.errorData = error.errorData;
        throw quotaError;
      }
      throw error;
    }
  }

  // Multipart upload for files >= 100MB
  private async multipartUpload(): Promise<void> {
    this.updateState({ uploadType: 'multipart' });

    try {
      // Request multipart upload initialization
      const response = await apiService.generatePresignedUpload(
        this.bucketName,
        this.objectKey,
        this.contentType,
        this.file.size
      );

      if (response.uploadType !== 'multipart') {
        throw new Error('Expected multipart upload response');
      }

      this.updateState({ uploadId: response.uploadId });

      try {
        // Upload chunks in parallel
        await this.uploadChunksInParallel(response.presignedUrls, response.partSize);

        // Complete multipart upload
        await apiService.completeMultipartUpload(
          this.bucketName,
          this.objectKey,
          response.uploadId,
          this.state.uploadedParts
        );
      } catch (error) {
        // Abort multipart upload on failure
        if (this.state.uploadId) {
          try {
            await apiService.abortMultipartUpload(
              this.bucketName,
              this.objectKey,
              this.state.uploadId
            );
          } catch (abortError) {
            console.error('Failed to abort multipart upload:', abortError);
          }
        }
        throw error;
      }
    } catch (error: any) {
      if (error.statusCode === 403 && (error.message?.includes('Storage limit exceeded') || error.message?.includes('Storage quota') || error.message?.includes('quota exceeded'))) {
        const quotaError: any = new Error(error.message || 'Storage limit exceeded. Please upgrade your plan to continue uploading.');
        quotaError.isStorageLimitError = true;
        quotaError.errorData = error.errorData;
        throw quotaError;
      }
      throw error;
    }
  }

  // Upload chunks in parallel with concurrency limit
  private async uploadChunksInParallel(
    presignedUrls: Array<{ partNumber: number; url: string }>,
    partSize: number
  ): Promise<void> {
    const queue = [...presignedUrls];
    const inProgress = new Set<Promise<void>>();

    while (queue.length > 0 || inProgress.size > 0) {
      // Fill up to MAX_CONCURRENT_UPLOADS
      while (inProgress.size < MAX_CONCURRENT_UPLOADS && queue.length > 0) {
        const urlInfo = queue.shift()!;
        const promise = this.uploadChunk(urlInfo, partSize);
        inProgress.add(promise);

        promise.finally(() => inProgress.delete(promise));
      }

      // Wait for at least one to complete
      if (inProgress.size > 0) {
        await Promise.race(inProgress);
      }
    }
  }

  // Upload a single chunk
  private async uploadChunk(
    urlInfo: { partNumber: number; url: string },
    partSize: number
  ): Promise<void> {
    const start = (urlInfo.partNumber - 1) * partSize;
    const end = Math.min(start + partSize, this.file.size);
    const chunk = this.file.slice(start, end);

    const response = await this.uploadWithRetry(urlInfo.url, chunk, undefined);
    const etag = response.headers.get('ETag');

    if (!etag) {
      throw new Error(`No ETag returned for part ${urlInfo.partNumber}`);
    }

    // Store uploaded part
    this.state.uploadedParts.push({
      PartNumber: urlInfo.partNumber,
      ETag: etag.replace(/"/g, ''),
    });

    // Sort parts by part number
    this.state.uploadedParts.sort((a, b) => a.PartNumber - b.PartNumber);

    this.updateProgress(end);
  }

  // Upload with POST and FormData (avoids CORS preflight)
  private async uploadWithPost(
    presignedPost: { url: string; fields: Record<string, string> },
    file: File,
    maxRetries: number = MAX_RETRIES
  ): Promise<void> {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const formData = new FormData();
        
        // Add all fields from presigned POST
        Object.entries(presignedPost.fields).forEach(([key, value]) => {
          formData.append(key, value);
        });
        
        // Add file last
        formData.append('file', file);
        
        const xhr = new XMLHttpRequest();
        
        const uploadPromise = new Promise<void>((resolve, reject) => {
          xhr.open('POST', presignedPost.url, true);
          
          // Track upload progress
          xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
              this.updateProgress(event.loaded);
            }
          };
          
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              this.updateProgress(file.size); // Ensure 100% progress
              resolve();
            } else {
              reject(new Error(`Upload failed with status ${xhr.status}`));
            }
          };
          
          xhr.onerror = () => reject(new Error('Network error'));
          xhr.onabort = () => {
            const abortError = new Error('Upload cancelled');
            abortError.name = 'AbortError';
            reject(abortError);
          };
          
          // Handle abort signal
          if (this.abortController.signal.aborted) {
            xhr.abort();
            const abortError = new Error('Upload cancelled');
            abortError.name = 'AbortError';
            reject(abortError);
          }
          
          this.abortController.signal.addEventListener('abort', () => {
            xhr.abort();
          });
          
          xhr.send(formData);
        });
        
        await uploadPromise;
        return;
      } catch (error) {
        lastError = error as Error;

        // Don't retry if aborted
        if (lastError.name === 'AbortError') {
          throw lastError;
        }

        // Exponential backoff: 1s, 2s, 4s
        if (attempt < maxRetries - 1) {
          await this.delay(Math.pow(2, attempt) * 1000);
        }
      }
    }

    throw lastError || new Error('Upload failed after retries');
  }

  // Upload with retry logic and exponential backoff
  private async uploadWithRetry(
    url: string,
    data: File | Blob,
    contentType?: string,
    maxRetries: number = MAX_RETRIES
  ): Promise<Response> {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        // Use XMLHttpRequest instead of fetch to avoid CORS preflight
        const xhr = new XMLHttpRequest();

        const uploadPromise = new Promise<Response>((resolve, reject) => {
          xhr.open('PUT', url, true);
          // Content-Type must match what was signed into the presigned URL
          if (contentType) xhr.setRequestHeader('Content-Type', contentType);

          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              // Create a Response-like object
              const response = {
                ok: true,
                status: xhr.status,
                headers: {
                  get: (name: string) => xhr.getResponseHeader(name)
                }
              } as Response;
              resolve(response);
            } else {
              reject(new Error(`Upload failed with status ${xhr.status}`));
            }
          };
          
          xhr.onerror = () => reject(new Error('Network error'));
          xhr.onabort = () => {
            const abortError = new Error('Upload cancelled');
            abortError.name = 'AbortError';
            reject(abortError);
          };
          
          // Handle abort signal
          if (this.abortController.signal.aborted) {
            xhr.abort();
            const abortError = new Error('Upload cancelled');
            abortError.name = 'AbortError';
            reject(abortError);
          }
          
          this.abortController.signal.addEventListener('abort', () => {
            xhr.abort();
          });
          
          xhr.send(data);
        });
        
        const response = await uploadPromise;

        if (!response.ok) {
          throw new Error(`Upload failed with status ${response.status}`);
        }

        return response;
      } catch (error) {
        lastError = error as Error;

        // Don't retry if aborted
        if (lastError.name === 'AbortError') {
          throw lastError;
        }

        // Exponential backoff: 1s, 2s, 4s
        if (attempt < maxRetries - 1) {
          await this.delay(Math.pow(2, attempt) * 1000);
        }
      }
    }

    throw lastError || new Error('Upload failed after retries');
  }

  // Update progress tracking
  private updateProgress(uploadedBytes: number): void {
    this.state.uploadedBytes = uploadedBytes;
    this.state.progress = (uploadedBytes / this.state.totalBytes) * 100;

    // Calculate estimated time remaining
    const elapsed = Date.now() - this.startTime;
    if (elapsed > 0 && uploadedBytes > 0) {
      const bytesPerMs = uploadedBytes / elapsed;
      const remainingBytes = this.state.totalBytes - uploadedBytes;
      this.state.estimatedTimeRemaining = remainingBytes / bytesPerMs;
    }

    this.notifyStateChange();
  }

  // Update state and notify listeners
  private updateState(updates: Partial<UploadState>): void {
    this.state = { ...this.state, ...updates };
    this.notifyStateChange();
  }

  // Notify state change listeners
  private notifyStateChange(): void {
    if (this.onStateChange) {
      this.onStateChange(this.getState());
    }
  }

  // Cancel upload
  async cancel(): Promise<void> {
    this.abortController.abort();

    // Abort multipart upload if in progress
    if (this.state.uploadType === 'multipart' && this.state.uploadId) {
      try {
        await apiService.abortMultipartUpload(
          this.bucketName,
          this.file.name,
          this.state.uploadId
        );
      } catch (error) {
        console.error('Failed to abort multipart upload:', error);
      }
    }

    this.updateState({ status: 'paused', error: 'Upload cancelled' });
  }

  // Utility: delay for specified milliseconds
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
